import React from 'react';

import ClearIcon from '@mui/icons-material/Clear';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useInterval } from '@tzmedical/react-hooks';

import axios from '../axiosClient.js';
import Alert from '../components/common/Alert.jsx';
import PageLoading from '../components/common/PageLoading.jsx';
import ExportOutstandingOrdersTMS from '../components/OutstandingOrdersTMS/ExportOutstandingOrdersTMS.jsx';
import OutstandingOrdersTMSTable from '../components/OutstandingOrdersTMS/OutstandingOrdersTMSTable.jsx';

// =============================================================================
// BLOCK OOTMS.1 — Page Configuration Constants
// =============================================================================
const REFRESH_INTERVAL_MS = 300_000; // 5 minutes
const ROWS_PER_PAGE_OPTIONS = [50, 250, 1000];
const DEFAULT_SORT_BY = 'ShipStatus';
const DEFAULT_SORT_DIR = 'asc';

function buildOrderTypeSearchText(search) {
  const raw = String(search || '').trim();
  if (!raw) {
    return '';
  }

  const normalized = raw.toLowerCase();
  const extras = new Set();

  if (
    normalized.includes('made to order') ||
    normalized.includes('mto') ||
    normalized.includes('backorder') ||
    normalized.includes('backordered')
  ) {
    extras.add('backordered');
    extras.add('made to order');
  }

  if (normalized.includes('processing')) {
    extras.add('processing');
  }

  if (
    normalized.includes('rep action required') ||
    normalized.includes('rep action') ||
    normalized.includes('action required')
  ) {
    extras.add('rep action required');
  }

  if (!extras.size) {
    return raw;
  }

  return `${raw} ${Array.from(extras).join(' ')}`;
}

// =============================================================================
// BLOCK OOTMS.2 — Main React Component
// =============================================================================
function OutstandingOrdersTMS() {
  // BLOCK OOTMS.2.2 — State for orders data and loading
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [ordersError, setOrdersError] = React.useState(null);

  // BLOCK OOTMS.2.3 — State for search and pagination
  const [searchText, setSearchText] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [totalItems, setTotalItems] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(1000);
  const [sortBy, setSortBy] = React.useState(DEFAULT_SORT_BY);
  const [sortDir, setSortDir] = React.useState(DEFAULT_SORT_DIR);
  const [hideCompletedOrders, setHideCompletedOrders] = React.useState(false);

  const normalizedSearchText = React.useMemo(
    () => buildOrderTypeSearchText(searchText),
    [searchText]
  );

  // ===========================================================================
  // BLOCK OOTMS.3 — Fetch Orders Callback
  // ===========================================================================
  const fetchOrders = React.useCallback(async () => {
    try {
      // BLOCK OOTMS.3.2 — Always use all-reps endpoint
      const url = '/api/outstanding-orders-tms/all-reps';

      const { data } = await axios({
        method: 'GET',
        url,
        params: {
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          search: normalizedSearchText || undefined,
          hideCompletedOrders: hideCompletedOrders || undefined,
          sortBy,
          sortDir,
        },
      });

      // BLOCK OOTMS.3.3 — Update state with results
      setOrders(data.orders);
      setTotalItems(data.total);
      setOrdersError(null);
      setLoading(false);
    } catch (err) {
      // BLOCK OOTMS.3.4 — Handle and log errors
      setOrdersError(err.response?.data?.error || err.message);
      setLoading(false);
    }
  }, [page, rowsPerPage, normalizedSearchText, hideCompletedOrders, sortBy, sortDir]);

  // ===========================================================================
  // BLOCK OOTMS.4 — Event Handlers
  // ===========================================================================

  // BLOCK OOTMS.4.1 — Handle search text changes
  const handleSearchChange = React.useCallback((event) => {
    setSearchText(event.target.value);
    setPage(0);
    setLoading(true);
  }, []);

  const handleClearSearch = React.useCallback(() => {
    setSearchText('');
    setPage(0);
    setLoading(true);
  }, []);

  // BLOCK OOTMS.4.2 — Handle page change in pagination
  const handleChangePage = React.useCallback((_, newPage) => {
    setPage(newPage);
    setLoading(true);
  }, []);

  // BLOCK OOTMS.4.3 — Handle rows per page change
  const handleChangeRowsPerPage = React.useCallback((event) => {
    const newRowsPerPage = Number(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setLoading(true);
  }, []);

  const handleHideCompletedOrdersChange = React.useCallback((event) => {
    setHideCompletedOrders(Boolean(event.target.checked));
    setPage(0);
    setLoading(true);
  }, []);

  // Rep selection removed - always using all-reps

  // BLOCK OOTMS.4.5 — Handle sortable header click
  const handleSort = React.useCallback(
    (nextSortBy) => {
      setPage(0);
      setLoading(true);

      if (nextSortBy === sortBy) {
        setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        return;
      }

      setSortBy(nextSortBy);
      setSortDir('asc');
    },
    [sortBy]
  );

  // ===========================================================================
  // BLOCK OOTMS.5 — Initial Fetch on Mount
  // =============================================================================
  // Initial load starts in the loading state so the page spinner renders without
  // triggering a React effect that synchronously sets state.

  // ===========================================================================
  // BLOCK OOTMS.5.1 — Auto-Refresh Hook
  // =============================================================================
  useInterval(fetchOrders, REFRESH_INTERVAL_MS, loading && !ordersError);

  // ===========================================================================
  // BLOCK OOTMS.6 — Compute Content Based on State
  // ===========================================================================
  const content = React.useMemo(() => {
    // BLOCK OOTMS.6.1 — Show loading indicator
    if (loading) {
      return <PageLoading />;
    }

    // BLOCK OOTMS.6.2 — Show error alert if fetch failed
    if (ordersError) {
      return (
        <Alert severity="error" title="Error Loading Orders">
          {ordersError}
        </Alert>
      );
    }

    // BLOCK OOTMS.6.3 — Show message if no orders found
    if (orders.length === 0) {
      return (
        <Typography
          variant="h6"
          align="center"
          sx={{ mt: 4, color: 'text.secondary' }}
          data-cy="no-orders-message"
        >
          No outstanding orders found.
        </Typography>
      );
    }

    // BLOCK OOTMS.6.4 — Render table and pagination
    return (
      <>
        <OutstandingOrdersTMSTable
          orders={orders}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
          data-cy="outstanding-orders-tms-table"
        />
        <TablePagination
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          data-cy="pagination-bottom"
        />
      </>
    );
  }, [
    loading,
    ordersError,
    orders,
    totalItems,
    rowsPerPage,
    page,
    sortBy,
    sortDir,
    handleSort,
    handleChangePage,
    handleChangeRowsPerPage,
  ]);

  // ===========================================================================
  // BLOCK OOTMS.7 — Render Main Page Layout
  // ===========================================================================
  return (
    <Box sx={{ p: 2 }} data-cy="outstanding-orders-tms-page">
      {/* BLOCK OOTMS.7.1 — Page Title */}
      <Typography variant="h4" sx={{ mb: 3 }}>
        Outstanding Orders (TMS)
      </Typography>

      {/* BLOCK OOTMS.7.2 — Top Controls Stack */}
      <Stack spacing={2} sx={{ mb: 3 }}>
        {/* BLOCK OOTMS.7.2.0 — Info message */}
        <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          Viewing all outstanding orders across all representatives
        </Typography>

        {/* BLOCK OOTMS.7.2.1 — Search and Export Controls */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Search TextField */}
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={handleSearchChange}
            placeholder="Order #, order type, item code, PO #, customer, rep..."
            slotProps={{
              input: {
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear search"
                      edge="end"
                      size="small"
                      onClick={handleClearSearch}
                      data-cy="outstanding-orders-tms-search-clear"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
            sx={{ flex: 1 }}
            data-cy="search-box"
          />

          {/* BLOCK OOTMS.7.2.2 — Export Button */}
          {orders.length > 0 && (
            <ExportOutstandingOrdersTMS
              searchText={searchText}
              hideCompletedOrders={hideCompletedOrders}
              data-cy="export-button"
            />
          )}
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={hideCompletedOrders}
                onChange={handleHideCompletedOrdersChange}
                size="small"
                data-cy="hide-completed-orders-checkbox"
              />
            }
            label="Hide Completed Orders"
          />
        </Box>

        {/* BLOCK OOTMS.7.3 — Top Pagination */}
        {orders.length > 0 && (
          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            data-cy="pagination-top"
          />
        )}
      </Stack>

      {/* BLOCK OOTMS.7.4 — Main Content Area */}
      {content}
    </Box>
  );
}

export default OutstandingOrdersTMS;
